import type { CurrentUser, SessionAdapter } from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import { invalidateEnterpriseBootstrap } from '@/enterprise/bootstrap';
import {
  createTenant as createTenantApi,
  deleteTenant as deleteTenantApi,
  getCurrentMemberUser,
  getTenantByCode as getTenantByCodeApi,
  getTenantList as getTenantListApi,
  joinTenant as joinTenantApi,
  logout as logoutApi,
  switchTenant as switchTenantApi,
} from '@/services/bff';
import type { TenantContext } from '@/services/bff/types/auth';
import { refreshTokensWithLock } from '@/services/oauth2';
import {
  getSessionSnapshot,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import {
  readCurrentTenantUuid,
  saveCurrentOrganizationUuid,
  saveCurrentTenantUuid,
} from './tenantStorage';

const updateTenantContext = (
  tenantUuid?: string | null,
  organizationUuid?: string | null,
) => {
  saveCurrentTenantUuid(tenantUuid);
  saveCurrentOrganizationUuid(organizationUuid);
  invalidateEnterpriseBootstrap(tenantUuid || undefined);
  const currentUser = getSessionSnapshot().currentUser;
  if (currentUser) {
    updateSessionSnapshot({
      currentUser: {
        ...currentUser,
        tenantUuid: tenantUuid || undefined,
        organizationUuid: organizationUuid || undefined,
      } as typeof currentUser,
    });
  }
  window.dispatchEvent(new Event('tenant-context-changed'));
  window.dispatchEvent(new Event('park-context-changed'));
};

// 缓存租户上下文的请求 Promise，防止初始化时 fetchCurrentUser 和 fetchTenantContexts 同时触发两个请求
let bootstrapTenantListPromise: Promise<TenantContext[]> | null = null;
let bootstrapTenantListSnapshot: TenantContext[] | null = null;

const normalizeTenantList = (tenantList?: TenantContext[] | null) => {
  const normalized = tenantList || [];
  if (!readCurrentTenantUuid()) {
    saveCurrentTenantUuid(normalized[0]?.id);
  }
  return normalized;
};

const fetchTenantListFromApi = async () => {
  const result = await getTenantListApi();
  return normalizeTenantList(result);
};

export const getBootstrapTenantList = async () => {
  if (bootstrapTenantListSnapshot) {
    return bootstrapTenantListSnapshot;
  }

  if (!bootstrapTenantListPromise) {
    bootstrapTenantListPromise = fetchTenantListFromApi()
      .then((tenantList) => {
        bootstrapTenantListSnapshot = tenantList;
        return tenantList;
      })
      .finally(() => {
        bootstrapTenantListPromise = null;
      });
  }

  return bootstrapTenantListPromise;
};

const consumeBootstrapTenantList = () => {
  const cached = bootstrapTenantListSnapshot;
  bootstrapTenantListSnapshot = null;
  return cached;
};

export const sessionAdapter: SessionAdapter = {
  fetchCurrentUser: async () => {
    const user = (await getCurrentMemberUser()) as CurrentUser &
      API.CurrentUser;
    updateTenantContext(user.tenantUuid, user.organizationUuid);

    return user;
  },
  fetchTenants: async () => {
    const cachedTenantList = consumeBootstrapTenantList();
    if (cachedTenantList) {
      return cachedTenantList;
    }

    const result = await getBootstrapTenantList();
    consumeBootstrapTenantList();
    return result;
  },
  switchTenant: async (organizationUuid: string) => {
    const result = await switchTenantApi(organizationUuid);
    await refreshTokensWithLock();
    updateTenantContext(result?.tenantUuid, result?.organizationUuid);
  },
  createTenant: async ({ tenantName }: { tenantName: string }) => {
    const result = await createTenantApi({ tenantName });
    updateTenantContext(result?.tenantUuid, result?.organizationUuid);
    return result;
  },
  lookupTenantByCode: async (code: string) => getTenantByCodeApi(code),
  joinTenant: async (payload: { tenantUuid: string; nodeId: string }) => {
    const result = await joinTenantApi(payload);
    updateTenantContext(result?.tenantUuid, result?.organizationUuid);
    return result;
  },
  deleteTenant: async (tenantUuid: string) => {
    const result = await deleteTenantApi(tenantUuid);
    updateTenantContext(
      result?.tenantUuid || null,
      result?.organizationUuid || null,
    );
    return result;
  },
  logout: async () => {
    const logoutBaseUrl = BizAppConfig.appSsoLogoutUrl;
    const logoutUrl = `${logoutBaseUrl}?redirect_uri=${encodeURIComponent(`${window.location.origin}/user/login`)}`;

    try {
      await logoutApi(BizAppConfig.appSsoClientId as string);
    } catch {
      // Keep local logout flow even when backend logout fails.
    } finally {
      // 登出时清除租户上下文缓存
    }

    updateTenantContext(null);
    return { logoutUrl };
  },
};
